import { ArrowUpRight, InfoIcon } from 'lucide-react';
import { Pie, PieChart } from 'recharts';
import { OptimizedImage } from '@/shared/ui/OptimizedImage';
import {
  ChartContainer,
  Card,
  CardContent,
  ChartTooltip,
  ChartTooltipContent,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import type {
  GwiSection,
  MediaConsumptionSectionProps,
  PlatformBarProps,
  RankedTableProps,
} from './MediaConsumptionSection.types';
import { SectionHeader } from '@/shared/ui/SectionHeader';
import { AudienceInsightsModal } from '@/shared/ui';
import { useModalState } from '@/shared/hooks';
import { useState } from 'react';
import { useBooleanFlagValue } from '@openfeature/react-sdk';
import type { GwiResearchCategory } from '@/entities/gwi-research';
import { useMediaConsumptionData } from './hooks/useMediaConsumptionData';
import { useTranslation } from 'react-i18next';

const MAX_RANKED_ITEMS = 5;

/** Maps each media-consumption section to the research category its Q&A history is scoped to. */
const SECTION_TO_RESEARCH_CATEGORY: Record<GwiSection, GwiResearchCategory> = {
  'Media Consumption': 'media_consumption',
  Platforms: 'top_platforms',
  'Content Types': 'top_content_types',
  Influencers: 'top_influencers',
  Channels: 'top_channels',
};

/**
 * Shares the header's inset, so a value sits under the word that names it.
 *
 * Carries its own type rather than inheriting the grid's: the donut's platform
 * bars and the ranked tables below them are two different grids showing the same
 * two measures, and they read as one set only while neither can drift.
 */
const MetricValue = ({ value }: { value: string }) => (
  <span className="px-2.5 text-sm font-medium text-muted-foreground">{value}</span>
);

/**
 * A `hint` sets an info icon after the label of a column whose heading does not
 * explain itself. Icon-only, so the tooltip carries the same words as its
 * `aria-label`: the picture is the invitation, not the label.
 */
const MetricHeader = ({ label, hint }: { label: string; hint?: string }) => {
  const heading = 'whitespace-nowrap px-2.5 text-left text-xs font-medium text-muted-foreground';

  if (!hint) return <span className={heading}>{label}</span>;

  return (
    <span className={`${heading} inline-flex items-center gap-1`}>
      {label}
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label={hint}
              className="text-muted-foreground outline-none hover:text-foreground focus-visible:focus-ring"
            >
              <InfoIcon className="size-3.5" />
            </button>
          </TooltipTrigger>
          <TooltipContent className="max-w-64">{hint}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </span>
  );
};

const PlatformBar = ({
  name,
  percentage,
  index,
  color,
  image,
  description,
  showIndex,
  showDescription,
}: PlatformBarProps) => (
  <div className="contents">
    <div className="flex items-center gap-2 text-muted-foreground text-sm">
      <div className="h-3 w-3 rounded-full" style={{ backgroundColor: color }} />
      {image && <OptimizedImage src={image} alt={name} width={20} height={20} />}
      <span className="text-foreground truncate">{name}</span>
    </div>
    <MetricValue value={`${percentage}%`} />
    {showIndex && (index !== undefined ? <MetricValue value={index.toFixed(1)} /> : <span />)}
    {showDescription && <span className="type-body-md-strong text-foreground">{description}</span>}
  </div>
);

const RankedTable = ({ title, items, section, onViewSection, showSource }: RankedTableProps) => {
  const { t } = useTranslation('audiences');

  if (items.length === 0) return null;

  // The reach column reflects GWI "Audience %" vs the LLM "Reach %"; `showSource`
  // is true only for GWI-sourced tables.
  const reachLabel = showSource
    ? t('audienceFlowWidget.mediaConsumptionSection.audience')
    : t('audienceFlowWidget.mediaConsumptionSection.reach');

  return (
    <div className="flex h-full flex-col rounded-xl bg-surface p-6">
      <h4 className="type-heading-xs text-foreground-subtle">{title}</h4>
      <div
        className="mt-4 grid items-center gap-x-6 gap-y-4 text-sm font-medium text-muted-foreground"
        style={{
          gridTemplateColumns:
            '1rem minmax(0, 1fr) minmax(5rem, max-content) minmax(5rem, max-content)',
        }}
      >
        <span />
        <span />
        <MetricHeader label={reachLabel} />
        <MetricHeader
          label={t('audienceFlowWidget.mediaConsumptionSection.index')}
          hint={t('audienceFlowWidget.mediaConsumptionSection.indexHint')}
        />
        {items.slice(0, MAX_RANKED_ITEMS).map((item, idx) => (
          <div key={item.name} className="contents">
            <span>{item.rank || idx + 1}</span>
            <span className="text-foreground truncate">{item.name}</span>
            <MetricValue value={`${item.reach.toFixed(1)}%`} />
            <MetricValue value={item.index.toFixed(1)} />
          </div>
        ))}
      </div>
      {showSource && (
        <button
          type="button"
          onClick={() => onViewSection(section)}
          className="mt-auto flex items-center gap-1 pt-5 self-start text-sm text-muted-foreground underline hover:text-foreground"
        >
          {t('audienceFlowWidget.mediaConsumptionSection.sourceCreatorPartnershipDatabase')}
          <ArrowUpRight className="size-3.5" />
        </button>
      )}
    </div>
  );
};

export const MediaConsumptionSection = ({ persona, exporting }: MediaConsumptionSectionProps) => {
  const { t } = useTranslation('audiences');

  // GWI stats only surface when the linking flag is on; otherwise we fall back
  // to the LLM-generated media consumption data.
  const gwiEnabled = useBooleanFlagValue('gwi_audience_linking', false);
  const { chartPlatforms, chartConfig, chartData, tableItems, hasGwiSection, gwiSectionRows } =
    useMediaConsumptionData(persona, gwiEnabled);

  const insightsModal = useModalState();
  const [activeSection, setActiveSection] = useState<GwiSection | null>(null);

  const handleViewSection = (section: GwiSection) => {
    setActiveSection(section);
    insightsModal.open();
  };

  const activeRows = gwiSectionRows(activeSection ?? 'Platforms');

  // The section names are GWI wire values, so the one that reaches the modal title has to be
  // swapped for a label rather than translated in place.
  const sectionLabels: Record<GwiSection, string> = {
    'Media Consumption': t('audienceFlowWidget.mediaConsumptionSection.section.mediaConsumption'),
    Platforms: t('audienceFlowWidget.mediaConsumptionSection.section.platforms'),
    'Content Types': t('audienceFlowWidget.mediaConsumptionSection.section.contentTypes'),
    Influencers: t('audienceFlowWidget.mediaConsumptionSection.section.influencers'),
    Channels: t('audienceFlowWidget.mediaConsumptionSection.section.channels'),
  };

  // GWI-sourced Media Consumption drives the chart's reach label and source link.
  const hasGwiMedia = hasGwiSection('Media Consumption');

  // Only render the Index / Description columns when at least one platform has
  // a value for them; drives both the header and the grid column template.
  const showChartIndex = chartPlatforms.some((platform) => platform.index !== undefined);
  const showChartDescription = chartPlatforms.some(
    (platform) => platform.description !== undefined,
  );

  // Columns: name, reach %, then Index / Description only when present.
  const chartGridColumns = [
    'minmax(7.5rem, max-content)',
    'max-content',
    showChartIndex && 'max-content',
    showChartDescription && 'minmax(0, 1fr)',
  ]
    .filter(Boolean)
    .join(' ');

  if (chartPlatforms.length === 0) {
    return null;
  }

  return (
    <section className="flex flex-col gap-6">
      <SectionHeader title={t('audienceFlowWidget.mediaConsumptionSection.mediaConsumption')} />

      {/* Pie Chart + Platform Bars */}
      <Card variant="outline" className="shadow-none border-border-light">
        <CardContent className="flex flex-col gap-3 w-full lg:flex-row ">
          <ChartContainer
            config={chartConfig}
            className="mx-auto aspect-square min-h-46.75 w-46.75"
            aria-label={t(
              'audienceFlowWidget.mediaConsumptionSection.donutChartShowingPlatformUsage',
            )}
          >
            <PieChart accessibilityLayer>
              {/* eslint-disable-next-line i18next/no-literal-string -- chart datum field name */}
              <ChartTooltip content={<ChartTooltipContent nameKey="platform" hideLabel />} />
              <Pie
                data={chartData}
                dataKey="value"
                nameKey="platform"
                innerRadius={46}
                outerRadius={72}
                strokeWidth={0}
                isAnimationActive={!exporting}
              />
            </PieChart>
          </ChartContainer>

          <div className="w-full lg:flex-1">
            <div
              className="grid items-center gap-x-10 gap-y-4 text-muted-foreground text-sm"
              style={{
                gridTemplateColumns: chartGridColumns,
                // The header keeps its own height; every platform row is a flexible
                // track, which on an auto-height grid equalises them to the tallest,
                // so a description that wraps does not make its row taller than the rest.
                gridTemplateRows: 'auto',
                gridAutoRows: 'minmax(0, 1fr)',
              }}
            >
              {/* Header row */}
              <span />
              <MetricHeader
                label={
                  hasGwiMedia
                    ? t('audienceFlowWidget.mediaConsumptionSection.audience')
                    : t('audienceFlowWidget.mediaConsumptionSection.reach')
                }
              />
              {showChartIndex && (
                <MetricHeader
                  label={t('audienceFlowWidget.mediaConsumptionSection.index')}
                  hint={t('audienceFlowWidget.mediaConsumptionSection.indexHint')}
                />
              )}
              {showChartDescription && (
                <span className="whitespace-nowrap text-xs">
                  {t('audienceFlowWidget.mediaConsumptionSection.description')}
                </span>
              )}

              {chartPlatforms.map((platform) => (
                <PlatformBar
                  key={platform.name}
                  {...platform}
                  showIndex={showChartIndex}
                  showDescription={showChartDescription}
                />
              ))}
            </div>

            {hasGwiMedia && (
              <button
                type="button"
                onClick={() => handleViewSection('Media Consumption')}
                className="flex items-center gap-1 self-start pt-5 text-sm text-muted-foreground underline hover:text-foreground"
              >
                {t('audienceFlowWidget.mediaConsumptionSection.sourceCreatorPartnershipDatabase')}
                <ArrowUpRight className="size-3.5" />
              </button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Media Consumption Tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <RankedTable
          title={t('audienceFlowWidget.mediaConsumptionSection.topPlatforms')}
          items={tableItems.Platforms}
          section="Platforms"
          onViewSection={handleViewSection}
          showSource={hasGwiSection('Platforms')}
        />
        <RankedTable
          title={t('audienceFlowWidget.mediaConsumptionSection.topContentTypes')}
          items={tableItems['Content Types']}
          section="Content Types"
          onViewSection={handleViewSection}
          showSource={hasGwiSection('Content Types')}
        />
        <RankedTable
          title={t('audienceFlowWidget.mediaConsumptionSection.topInfluencers')}
          items={tableItems.Influencers}
          section="Influencers"
          onViewSection={handleViewSection}
          showSource={hasGwiSection('Influencers')}
        />
        <RankedTable
          title={t('audienceFlowWidget.mediaConsumptionSection.topChannels')}
          items={tableItems.Channels}
          section="Channels"
          onViewSection={handleViewSection}
          showSource={hasGwiSection('Channels')}
        />
      </div>

      {insightsModal.isMounted && activeSection && (
        <AudienceInsightsModal
          open={insightsModal.isOpen}
          section={sectionLabels[activeSection]}
          subtitle={persona.segmentName ?? ''}
          insights={activeRows}
          showPlatformIcons={activeSection === 'Platforms' || activeSection === 'Media Consumption'}
          personaId={persona.id}
          category={SECTION_TO_RESEARCH_CATEGORY[activeSection]}
          onClose={insightsModal.close}
        />
      )}
    </section>
  );
};
